
import api from '../../../services/api';
const URL = '/todos';

class TodoService {
    getTodos(params = {}) {
        return api.get(URL, { params });
    }

    /*
    | No trailing slashes on these paths.
    |
    | `/todos/{id}/` does not match `Route::put('todos/{id}')`. Laravel only redirects a
    | trailing slash for GET/HEAD; for other verbs the web server in front of it issues a
    | 301 instead, and a 301 on a preflighted PUT arrives at the browser WITHOUT CORS
    | headers — so a routing problem is reported as a CORS failure, which is why this
    | looked like a CORS issue while POST and PATCH calls on slash-free paths worked.
    */
    getTodo(id) {
        return api.get(`${URL}/${id}`);
    }

    createTodo(data) {
        return api.post(URL, data);
    }

    updateTodo(id, data) {
        return api.put(`${URL}/${id}`, data);
    }

    deleteTodo(id) {
        return api.delete(`${URL}/${id}`);
    }

    toggleCompleted(id) {
        return api.patch(`${URL}/${id}/toggle_completed`);
    }

    toggleStarred(id) {
        return api.patch(`${URL}/${id}/toggle_starred`);
    }

    reorderTodos(reorderData) {
        return api.post(`${URL}/reorder`, { reorder_data: reorderData });
    }

    /**
     * Board drag: new column and position in ONE request.
     * Split calls would leave the card visibly wrong if the second failed.
     */
    moveTodo(id, status, position = null) {
        return api.patch(`${URL}/${id}/move`, { status, position });
    }

    /**
     * Statuses, license-filtered link types, assignable users, and whether this user
     * may see the team board — one request, so the editor opens without a waterfall.
     */
    getOptions() {
        return api.get(`${URL}/options`);
    }

    getStatistics() {
        return api.get(`${URL}/statistics`);
    }

    /*
    | Comments and attachments reuse the PLATFORM-WIDE endpoints with model_type='todo'
    | rather than todo-specific ones. Same storage quota, same upload pipeline, same
    | comment thread component as everywhere else.
    */
    getComments(todoPublicId) {
        return api.get(`/comments/todo/${todoPublicId}`);
    }

    addComment(todoPublicId, body) {
        return api.post('/comments', { model_type: 'todo', model_id: todoPublicId, body });
    }

    /* Checklist steps. Scoped under the task, which is also where authorisation is
       resolved server-side — an item is only reachable via a task you can see. */
    getChecklist(todoPublicId) {
        return api.get(`${URL}/${todoPublicId}/checklist`);
    }

    addChecklistItem(todoPublicId, title) {
        return api.post(`${URL}/${todoPublicId}/checklist`, { title });
    }

    updateChecklistItem(todoPublicId, itemId, data) {
        return api.put(`${URL}/${todoPublicId}/checklist/${itemId}`, data);
    }

    deleteChecklistItem(todoPublicId, itemId) {
        return api.delete(`${URL}/${todoPublicId}/checklist/${itemId}`);
    }

    /* Task history, via the platform's shared audit trail. */
    getAuditTrail(todoPublicId) {
        return api.get(`${URL}/${todoPublicId}/audit_trail`);
    }

    /* Platform-wide comment endpoints — author-only edit/delete is enforced server-side. */
    updateComment(commentPublicId, body) {
        return api.put(`/comments/${commentPublicId}`, { body });
    }

    deleteComment(commentPublicId) {
        return api.delete(`/comments/${commentPublicId}`);
    }

    getAttachments(todoPublicId) {
        return api.get(`/media/todo/${todoPublicId}`);
    }

    uploadAttachment(todoPublicId, file, displayName = null) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('model_type', 'todo');
        formData.append('model_id', todoPublicId);
        if (displayName) formData.append('display_name', displayName);

        return api.post('/media', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }
}

export default new TodoService()